<template>
  <v-container class="content" >
    <v-row v-if="getLogoCss" no-gutters v-bind:style="getLogoCss" class="mb-5">
      <a
        v-if="getHomeUrl"
        :href="getHomeUrl"
        :aria-label="getBrandingInfo?.name || getHomeUrl"
        style="display: block; width: 100%; height: 100%;"
        target="_blank"
      />
    </v-row>
    <v-row v-else no-gutters justify="center" align="center" class="login-logo mb-5">
      <LoginLogo />
    </v-row>

    <v-row v-if="linkError" no-gutters justify="center" align="center"
           id="accesslink-login-link-error"
           ref="linkErrorMessage"
           class="text-center mb-5 inline-message--error"
           role="alert"
           tabindex="-1">
      {{ linkError }}
    </v-row>

    <v-row
      v-if="errorMessage"
      justify="center"
      align="center"
      id="accesslink-login-error"
      ref="errorMessage"
      class="text-center mb-5 inline-message--error"
      no-gutters
      role="alert"
      tabindex="-1"
    >
      {{ errorMessage }}
    </v-row>

    <v-form
      ref="form"
      v-bind:disabled="formDisabled"
      v-on:submit.prevent="login"
      data-cy="accessLink-auth-form">
      <v-row>
        <v-text-field
          ref="passwordField"
          id="accesslink-login-password"
          v-bind:rules="passwordRules"
          v-model="password"
          type="password"
          label="Password"
          placeholder="Password"
          autocomplete="current-password"
          prepend-icon="mdi-lock"
          variant="solo-filled"
          elevation="0"
          block
          required
          data-cy="accessLink-auth-password">
        </v-text-field>
      </v-row>

      <v-row class="mt-5">
        <v-btn
          type="submit"
          v-bind:disabled="formDisabled"
          :elevation="0"
          block
          color="primary"
          variant="flat"
          data-cy="accessLink-auth-authorize-btn"
        >
          {{ $t('routes.accesslink.authorize') }}
        </v-btn>
      </v-row>
    </v-form>

  </v-container>
</template>

<script>
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import LoginLogo from "../account/components/LoginLogo.vue"
import { useTheme } from 'vuetify';

export default {
  name: "AccesslinkLogin",
  components: {
    LoginLogo,
  },
  data: function() {
    return {
      code: "",
      linkError: "",

      errorMessage: "",
      password: "",
      passwordRules: [
        v => !!v || this.$t('menus.createUser.passwordRequired'),
        // v => (v && v.length >= 8) || "Password should be more than 8 symbols"
      ],
      theme: null,
    };
  },
  mounted: function() {
    this.theme = useTheme();
    this.code = this.$route.params.code;

    if (
      this.getBrandingInfo &&
      this.getBrandingInfo.theme === 'light'
    ) {
      this.theme.change('light');
    } else {
      this.theme.change('dark');
    }

    if (this.getBrandingInfo) {
      this.updateColors(this.getBrandingInfo);
    }

    this.$recaptchaLoaded('bottomleft')
      .then(() => this.$recaptchaShowBadge())
      .catch((err) => {
        this.linkError = `Unable to load ReCaptcha - ${err.message}`;
        this.focusLinkError();
      });

    const accesslinkStore = useAccesslinkStore();
    accesslinkStore.refreshAccesslinkInfo(this.code)
      .then( (data) => {
        if (!data.needPassword) {
          return accesslinkStore.refreshAccesslinkContents();
        }
      })
      .then(() => {
        this.redirectToAccessLinkContents();
      })
      .catch( (error) => {
        if (error &&
          error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message
        ) {
          this.linkError = error.response.data.error.message;
        } else {
          console.log(error)
          this.linkError = "Error!";
        }

        this.focusLinkError();
      });
  },
  watch: {
    getBrandingInfo: {
      handler(newValue) {
        this.updateColors(newValue);
      },
      deep: true
    }
  },
  methods: {
    updateColors(newValue) {
      if (newValue && newValue.color) {
        this.$vuetify.theme.themes.dark.colors.primary = newValue.color;
        this.$vuetify.theme.themes.light.colors.primary = newValue.color;

        this.$vuetify.theme.themes.dark.colors['on-list'] = newValue.color;
        this.$vuetify.theme.themes.light.colors['on-list'] = newValue.color;
      }

      if (newValue && newValue.backgroundColor) {
        this.$vuetify.theme.themes.dark.colors.background = newValue.backgroundColor;
        this.$vuetify.theme.themes.light.colors.background = newValue.backgroundColor;
      }

      if (newValue && newValue.foregroundColor) {
        this.$vuetify.theme.themes.dark.colors['tw-surface'] = newValue.foregroundColor;
        this.$vuetify.theme.themes.light.colors['tw-surface'] = newValue.foregroundColor;

        this.$vuetify.theme.themes.dark.colors['surface'] = newValue.foregroundColor;
        this.$vuetify.theme.themes.light.colors['surface'] = newValue.foregroundColor;
      }

      if (newValue && newValue.foregroundAccentColor) {
        this.$vuetify.theme.themes.dark.colors['tw-surface-accent'] = newValue.foregroundAccentColor;
        this.$vuetify.theme.themes.light.colors['tw-surface-accent'] = newValue.foregroundAccentColor;

        this.$vuetify.theme.themes.dark.colors['list'] = newValue.foregroundAccentColor;
        this.$vuetify.theme.themes.light.colors['list'] = newValue.foregroundAccentColor;
      }

      if (newValue && newValue.theme && newValue.theme === 'light') {
        this.theme.change('light');
      }
      if (newValue && newValue.theme && newValue.theme === 'dark') {
        this.theme.change('dark');
      }
    },
    login: async function() {
      const validateFormResult = await this.$refs.form.validate();
      if (!validateFormResult.valid) {
        this.focusPasswordField();
        return;
      }

      const formData = {
        code: this.code,
        password: this.password,
        email: this.email,
      };

      const accesslinkStore = useAccesslinkStore();
      accesslinkStore.authorizeAccesslink(formData)
        .then(() => {
          return accesslinkStore.refreshAccesslinkContents();
        })
        .then(() => {
          this.redirectToAccessLinkContents();
        })
        .catch((error) => {
          if (error &&
            error.response &&
            error.response.data &&
            error.response.data.error &&
            error.response.data.error.message) {
            this.errorMessage = error.response.data.error.message;
            this.focusFormError();
          } else {
            console.error(error);
            this.linkError = "Error!";
            this.focusLinkError();
          }
        });
    },
    focusPasswordField() {
      this.$nextTick(() => {
        this.$refs.passwordField?.focus?.();
      });
    },
    focusLinkError() {
      this.$nextTick(() => {
        this.$refs.linkErrorMessage?.$el?.focus?.();
      });
    },
    focusFormError() {
      this.$nextTick(() => {
        this.$refs.errorMessage?.$el?.focus?.();
      });
    },
    redirectToAccessLinkContents() {
      const accessLinkPods = this.getAccessLinkPods;
      const accessLinkNodes = this.getAccessLinkNodes;

      if (accessLinkNodes.length === 0 && accessLinkPods.length === 1) {
        this.$router.replace({
          name: 'accesslink:view:pod',
          params: { podid: accessLinkPods[0].id },
        })
      } else if (accessLinkPods.length === 0 && accessLinkNodes.length === 1 && accessLinkNodes[0].pods.length === 1) {
        this.$router.replace({
          name: 'accesslink:view:pod',
          params: { podid: accessLinkNodes[0].pods[0].id, nodeid: accessLinkNodes[0].id },
        });
      } else if (accessLinkPods.length > 0 || accessLinkNodes.length > 0) {
        this.$router.replace({
          name: 'accesslink:view',
        });
      }
    },
  },
  computed: {
    getAccesslinkInfo: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    getAccessLinkPods: function () {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkPods;
    },
    getAccessLinkNodes: function () {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkNodes;
    },
    getBrandingInfo: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.brand) {
        return this.getAccesslinkInfo.brand;
      }

      return null;
    },
    getLogoCss: function() {
      if (this.getBrandingInfo && this.getBrandingInfo.logoUrl) {
        return {
          width: '150px',
          height: '150px',
          margin: 'auto',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundImage: 'url("'+this.getBrandingInfo.logoUrl+'")',
        };
      }

      return null;
    },
    getHomeUrl: function() {
      if (!this.getBrandingInfo || !this.getBrandingInfo.alias) {
        return null;
      }

      const homeURL = this.getBrandingInfo.alias;
      if (!/^https?:\/\//gmi.test(homeURL)) {
        return "http://" + homeURL;
      }

      return homeURL;
    },
    formDisabled: function() {
      return !!this.linkError;
    },
    email: function() {
      if (this.$route.query.e) {
        return this.$route.query.e;
      }

      return '';
    }
  }
}
</script>

<style>

</style>
